import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import starWebPRNTService from '../../services/starWebPRNTService';

/**
 * StarWebPRNT プリンタ設定画面
 * 店舗管理画面から使用。プリンタIPの設定・接続テスト・テスト印刷。
 */
const PrinterSettings = () => {
  const [settings, setSettings] = useState(starWebPRNTService.getPrinterSettings());
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState({ receipt: false, kitchen: false });
  const [testResult, setTestResult] = useState({ receipt: null, kitchen: null });

  useEffect(() => {
    setSettings(starWebPRNTService.getPrinterSettings());
  }, []);

  const handleSave = () => {
    starWebPRNTService.savePrinterSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleTestConnection = async (type) => {
    const printerConfig = type === 'receipt' ? settings.receiptPrinter : settings.kitchenPrinter;
    setTesting((prev) => ({ ...prev, [type]: true }));
    setTestResult((prev) => ({ ...prev, [type]: null }));

    const result = await starWebPRNTService.checkPrinterStatus(printerConfig);
    setTestResult((prev) => ({ ...prev, [type]: result }));
    setTesting((prev) => ({ ...prev, [type]: false }));
  };

  const handleTestPrint = async (type) => {
    setTesting((prev) => ({ ...prev, [type]: true }));

    // 設定を一時保存してからテスト印刷
    starWebPRNTService.savePrinterSettings(settings);

    if (type === 'receipt') {
      const result = await starWebPRNTService.printReceipt({
        storeName: 'POSplus テスト店舗',
        storeAddress: '東京都渋谷区テスト1-2-3',
        storePhone: '03-1234-5678',
        invoiceNumber: 'T1234567890123',
        date: new Date().toLocaleString('ja-JP'),
        tableNumber: '5',
        items: [
          { name: 'テスト商品A', quantity: 2, price: 500 },
          { name: 'テスト商品B', quantity: 1, price: 800 },
        ],
        subtotal: 1800,
        tax: 180,
        total: 1980,
        paymentMethod: '現金',
        receivedAmount: 2000,
        change: 20,
      });
      setTestResult((prev) => ({ ...prev, receipt: result.success ? { connected: true } : { connected: false, reason: result.error } }));
    } else {
      const result = await starWebPRNTService.printKitchenTicket({
        tableNumber: '5',
        items: [
          { name: 'テスト商品A', quantity: 2 },
          { name: 'テスト商品B', quantity: 1, note: '辛さ控えめ' },
        ],
      });
      setTestResult((prev) => ({ ...prev, kitchen: result.success ? { connected: true } : { connected: false, reason: result.error } }));
    }

    setTesting((prev) => ({ ...prev, [type]: false }));
  };

  const updatePrinter = (type, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const renderPrinterSection = (type, label) => {
    const config = type === 'receiptPrinter' ? settings.receiptPrinter : settings.kitchenPrinter;
    const testKey = type === 'receiptPrinter' ? 'receipt' : 'kitchen';
    const result = testResult[testKey];
    const isTesting = testing[testKey];

    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            <PrintIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {label}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                onChange={(e) => updatePrinter(type, 'enabled', e.target.checked)}
              />
            }
            label="有効"
          />
        </Box>

        {config.enabled && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="プリンタIPアドレス"
                  placeholder="192.168.1.100"
                  value={config.ip}
                  onChange={(e) => updatePrinter(type, 'ip', e.target.value)}
                  size="small"
                  helperText="Star mC-Print3 のIPアドレスを入力"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="ポート"
                  type="number"
                  value={config.port}
                  onChange={(e) => updatePrinter(type, 'port', Number(e.target.value))}
                  size="small"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleTestConnection(testKey)}
                disabled={!config.ip || isTesting}
                startIcon={isTesting ? <CircularProgress size={16} /> : null}
              >
                接続テスト
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleTestPrint(testKey)}
                disabled={!config.ip || isTesting}
                startIcon={<PrintIcon />}
              >
                テスト印刷
              </Button>
            </Box>

            {result && (
              <Box sx={{ mt: 1 }}>
                {result.connected ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="接続OK"
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`接続失敗: ${result.reason || '不明'}`}
                    color="error"
                    size="small"
                  />
                )}
              </Box>
            )}
          </>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        プリンタ設定（StarWebPRNT）
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Star mC-Print3 にLAN経由で直接印刷します。Electronアプリは不要です。
      </Typography>

      {renderPrinterSection('receiptPrinter', 'レシートプリンタ')}
      {renderPrinterSection('kitchenPrinter', 'キッチンプリンタ')}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>印刷オプション</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={settings.autoCut}
              onChange={(e) => setSettings((prev) => ({ ...prev, autoCut: e.target.checked }))}
            />
          }
          label="オートカット"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.openDrawer}
              onChange={(e) => setSettings((prev) => ({ ...prev, openDrawer: e.target.checked }))}
            />
          }
          label="現金決済時にキャッシュドロアを開く"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.autoKitchenPrint !== false}
              onChange={(e) => setSettings((prev) => ({ ...prev, autoKitchenPrint: e.target.checked }))}
            />
          }
          label="注文時にキッチン伝票を自動印刷"
        />
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleSave}
        sx={{ mb: 1 }}
      >
        設定を保存
      </Button>

      {saved && (
        <Alert severity="success">設定を保存しました</Alert>
      )}
    </Box>
  );
};

export default PrinterSettings;
